"use client";

import { useMemo, useState } from "react";
import NavigatorSectionToolbar from "./navigator/NavigatorSectionToolbar";
import NavigatorFilesSection from "./navigator/NavigatorFilesSection";
import NavigatorAgentSection from "./navigator/NavigatorAgentSection";
import NavigatorSettingsSection from "./navigator/NavigatorSettingsSection";
import NavigatorInputsPreviewSection from "./navigator/NavigatorInputsPreviewSection";
import NavigatorDocsSection from "./navigator/NavigatorDocsSection";
import {
  DEFAULT_NAVIGATOR_SECTION,
  type NavigatorSection,
} from "./navigator/navigatorTypes";
import { useCustomIndicatorProjectStore } from "@/lib/custom-indicators/store";
import { buildTree, getEntryContent } from "@/lib/custom-indicators/project-fs";

export default function CustomIndicatorEditorNavigator() {
  const [section, setSection] = useState<NavigatorSection>(
    DEFAULT_NAVIGATOR_SECTION
  );

  const project = useCustomIndicatorProjectStore((s) => s.project);
  const files = useCustomIndicatorProjectStore((s) => s.files);
  const dirtyFiles = useCustomIndicatorProjectStore((s) => s.dirtyFiles);
  const activeEditorTarget = useCustomIndicatorProjectStore(
    (s) => s.activeEditorTarget
  );
  const activeFilePath =
    activeEditorTarget.kind === "file" ? activeEditorTarget.path : "";
  const setActiveFile = useCustomIndicatorProjectStore((s) => s.setActiveFile);
  const createFile = useCustomIndicatorProjectStore((s) => s.createFile);
  const createFolder = useCustomIndicatorProjectStore((s) => s.createFolder);
  const renameFileOrFolder = useCustomIndicatorProjectStore(
    (s) => s.renameFileOrFolder
  );
  const deleteFileOrFolder = useCustomIndicatorProjectStore(
    (s) => s.deleteFileOrFolder
  );
  const moveFileOrFolder = useCustomIndicatorProjectStore(
    (s) => s.moveFileOrFolder
  );
  const copyFileOrFolder = useCustomIndicatorProjectStore(
    (s) => s.copyFileOrFolder
  );
  const setEntryFile = useCustomIndicatorProjectStore((s) => s.setEntryFile);
  const importIconFile = useCustomIndicatorProjectStore((s) => s.importIconFile);
  const updateProjectMeta = useCustomIndicatorProjectStore(
    (s) => s.updateProjectMeta
  );

  const tree = useMemo(
    () => (project ? buildTree(files, project.name) : null),
    [files, project]
  );

  const entryCode = useMemo(() => {
    if (!project) return "";
    const merged = files.map((f) => {
      const dirty = dirtyFiles[f.path];
      return dirty && f.type === "file" ? { ...f, content: dirty.content } : f;
    });
    return getEntryContent(merged, project.entryFilePath);
  }, [files, dirtyFiles, project]);

  return (
    <div className="flex h-full min-h-0 flex-col border-r border-border">
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-2 py-1">
        <span className="truncate text-xs text-muted-foreground">
          Navigator
        </span>
        <NavigatorSectionToolbar
          section={section}
          onSectionChange={setSection}
        />
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {section === "files" ? (
          <NavigatorFilesSection
            tree={tree}
            files={files}
            activeFilePath={activeFilePath}
            entryFilePath={project?.entryFilePath ?? "Indicator.cs"}
            onSelectFile={setActiveFile}
            onCreateFile={createFile}
            onCreateFolder={createFolder}
            onRename={renameFileOrFolder}
            onDelete={deleteFileOrFolder}
            onMove={moveFileOrFolder}
            onCopy={copyFileOrFolder}
            onSetEntryFile={(path) => {
              try {
                setEntryFile(path);
              } catch (e) {
                window.alert(
                  e instanceof Error ? e.message : "Could not set entry file"
                );
              }
            }}
          />
        ) : null}
        {section === "agent" ? <NavigatorAgentSection /> : null}
        {section === "settings" && project ? (
          <NavigatorSettingsSection
            project={project}
            onUpdateMeta={updateProjectMeta}
            onImportIcon={importIconFile}
          />
        ) : null}
        {section === "inputs" ? (
          <NavigatorInputsPreviewSection code={entryCode} />
        ) : null}
        {section === "docs" ? <NavigatorDocsSection /> : null}
      </div>
    </div>
  );
}
